import { useCallback, useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';
import { useTabs } from '../context/TabContext';

const MAX_FRAMES = 180;
const MOBILE_MAX_FRAMES = 60;

const keyBase = (tabKey, gridId) => `psScroll:${tabKey || 'default'}:${gridId}`;
const posKeyFor = (tabKey, gridId) => `${keyBase(tabKey, gridId)}:pos`;
const backKeyFor = (tabKey, gridId) => `${keyBase(tabKey, gridId)}:back`;
const stateKeyFor = (tabKey, gridId) => `${keyBase(tabKey, gridId)}:state`;

/**
 * Mark that the next mount of `gridId` in the given in-app tab should restore its
 * saved scroll position - for explicit "back to the list" controls that navigate
 * with a fresh PUSH (e.g. the ticket page's "Back to tickets" button). Browser
 * Back/Forward is detected on its own and does not need this.
 */
export const requestGridScrollRestore = (gridId, tabKey) => {
  sessionStorage.setItem(backKeyFor(tabKey, gridId), '1');
};

/**
 * Shared save/restore policy. The caller supplies how to read the current
 * position (`capturePosition`) and how to put it back (`applyPosition`, which may
 * return a cleanup fn); this hook owns *when* that happens:
 *   - saves on a row click (via the returned saveScrollAnchor) and on any in-app
 *     tab change while the list is mounted,
 *   - restores once per mount, and only when the arrival was a genuine "back":
 *     browser Back/Forward (navigationType 'POP', captured as the page was
 *     entered) or an explicit requestGridScrollRestore() marker,
 *   - consumes the saved state on every mount, so a nav-menu link, a typed URL
 *     or a status / date / filter change opens the list at the top.
 *
 * Keys are namespaced per in-app tab (two tabs on the same list keep independent
 * positions) and, via sessionStorage, per browser tab.
 */
const useScrollRestore = ({ gridId, ready, capturePosition, applyPosition }) => {
  const navigationType = useNavigationType();
  // The navigation type as the page was entered - a later in-page replace()
  // (the mobile list syncs ?status= on mount) must not mask a real Back.
  const arrivalNavType = useRef(navigationType).current;
  const tabs = useTabs();
  const tabKey = tabs?.activeTab?.key;
  const restoreHandled = useRef(false);

  const posKey = posKeyFor(tabKey, gridId);
  const backKey = backKeyFor(tabKey, gridId);

  const saveScrollAnchor = useCallback(() => {
    const position = capturePosition();
    if (position != null) sessionStorage.setItem(posKey, JSON.stringify(position));
  }, [capturePosition, posKey]);

  // Save on any in-app tab change while this list is mounted, and flag it as a
  // return so the restore fires when the user comes back to this tab.
  useEffect(() => {
    const onTabChange = () => {
      saveScrollAnchor();
      sessionStorage.setItem(backKey, '1');
    };
    window.addEventListener('tabchange', onTabChange);
    return () => window.removeEventListener('tabchange', onTabChange);
  }, [saveScrollAnchor, backKey]);

  useEffect(() => {
    // Wait until the list is actually ready - rows can load after mount, which
    // flips `ready` false->true.
    if (restoreHandled.current || !ready) return undefined;
    restoreHandled.current = true;

    const backRequested = sessionStorage.getItem(backKey) === '1';
    const shouldRestore = arrivalNavType === 'POP' || backRequested;

    let savedPosition = null;
    try {
      savedPosition = JSON.parse(sessionStorage.getItem(posKey));
    } catch {
      savedPosition = null;
    }

    // One-shot: every mount consumes the saved state.
    sessionStorage.removeItem(posKey);
    sessionStorage.removeItem(backKey);

    if (!shouldRestore || savedPosition == null) return undefined;
    return applyPosition(savedPosition);
  }, [ready, posKey, backKey, arrivalNavType, applyPosition]);

  return { saveScrollAnchor };
};

/**
 * DataGridPro (desktop) scroll restore. Position is the top visible row + its
 * pixel offset; see useScrollRestore for when it saves and restores.
 *
 * @param {object}  params
 * @param {string}  params.gridId  stable id used to namespace the storage keys
 * @param {object}  params.apiRef  the DataGridPro apiRef (from useGridApiRef)
 * @param {boolean} params.ready   true once rows are loaded and the grid is mounted
 * @returns {{ saveScrollAnchor: () => void }}
 */
export const useGridScrollRestore = ({ gridId, apiRef, ready }) => {
  // The first rendered row whose bottom edge is still inside the scroller
  // viewport is the row the current scroll rests on; record how far its top sits
  // relative to (usually just above) the viewport top. data-id is a string, and
  // getRowElement / getRowIndexRelativeToVisibleRows both accept it that way.
  const capturePosition = useCallback(() => {
    const rootElement = apiRef.current?.rootElementRef?.current;
    const scrollerElement = rootElement?.querySelector('.MuiDataGrid-virtualScroller');
    if (!scrollerElement) return null;

    const scrollerTop = scrollerElement.getBoundingClientRect().top;
    const rowElements = scrollerElement.querySelectorAll('.MuiDataGrid-row');
    for (let i = 0; i < rowElements.length; i += 1) {
      const rect = rowElements[i].getBoundingClientRect();
      if (rect.bottom > scrollerTop + 1) {
        const rowId = rowElements[i].getAttribute('data-id');
        return rowId == null ? null : { rowId, offset: rect.top - scrollerTop };
      }
    }
    return null;
  }, [apiRef]);

  const applyPosition = useCallback(
    (savedPosition) => {
      if (savedPosition.rowId == null || !apiRef.current) return undefined;
      const { rowId, offset: savedOffset } = savedPosition;

      let frame;
      let lastRectTop;
      let stableFrames = 0;
      let attempts = 0;

      // rows can use getRowHeight="auto" and virtualization only renders /
      // measures rows once they have actually entered the viewport, so a single
      // jump to a distant row relies on estimated heights for everything skipped
      // above it; keep re-scrolling each frame until the row's real rendered
      // position stops moving, then place it at the exact spot (savedOffset) it
      // sat before.
      const scrollToRow = () => {
        attempts += 1;
        if (!apiRef.current || attempts > MAX_FRAMES) return;

        const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(rowId);
        if (typeof rowIndex !== 'number' || rowIndex < 0) {
          frame = requestAnimationFrame(scrollToRow);
          return;
        }
        apiRef.current.scrollToIndexes({ rowIndex });

        const rowElement = apiRef.current.getRowElement(rowId);
        const scrollerElement = rowElement?.closest('.MuiDataGrid-virtualScroller');
        if (!rowElement || !scrollerElement) {
          frame = requestAnimationFrame(scrollToRow);
          return;
        }

        const rowRectTop = rowElement.getBoundingClientRect().top;
        if (Math.abs(rowRectTop - (lastRectTop ?? Infinity)) < 0.5) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          lastRectTop = rowRectTop;
        }

        if (stableFrames < 3) {
          frame = requestAnimationFrame(scrollToRow);
          return;
        }

        const scrollerRectTop = scrollerElement.getBoundingClientRect().top;
        const contentOffset = rowRectTop - scrollerRectTop + scrollerElement.scrollTop;
        const maxScrollTop = scrollerElement.scrollHeight - scrollerElement.clientHeight;
        scrollerElement.scrollTop = Math.max(0, Math.min(contentOffset - savedOffset, maxScrollTop));
      };

      frame = requestAnimationFrame(scrollToRow);
      return () => cancelAnimationFrame(frame);
    },
    [apiRef]
  );

  return useScrollRestore({
    gridId,
    ready: ready && !!apiRef.current,
    capturePosition,
    applyPosition,
  });
};

/**
 * react-window FixedSizeList (mobile) scroll restore. Position is the plain
 * scrollTop of the list's outer element; see useScrollRestore for when it saves
 * and restores.
 *
 * @param {object}  params
 * @param {string}  params.gridId            stable id used to namespace the storage keys
 * @param {object}  params.scrollElementRef  ref to the list's scroll container (react-window outerRef)
 * @param {boolean} params.ready             true once rows are loaded and the list is mounted
 * @returns {{ saveScrollAnchor: () => void }}
 */
export const useMobileListScrollRestore = ({ gridId, scrollElementRef, ready }) => {
  const capturePosition = useCallback(
    () => ({ offset: scrollElementRef.current?.scrollTop ?? 0 }),
    [scrollElementRef]
  );

  const applyPosition = useCallback(
    (savedPosition) => {
      const target = Math.max(0, Number(savedPosition?.offset) || 0);
      if (!target) return undefined;

      let frame;
      let attempts = 0;

      // the list height comes from AutoSizer and its rows can still be filling
      // in, so scrollHeight grows over a few frames; keep setting scrollTop until
      // the list is tall enough to honour it (or we give up).
      const step = () => {
        attempts += 1;
        const element = scrollElementRef.current;
        if (element) {
          const maxTop = Math.max(0, element.scrollHeight - element.clientHeight);
          element.scrollTop = Math.min(target, maxTop);
          if (maxTop >= target || attempts >= MOBILE_MAX_FRAMES) return;
        } else if (attempts >= MOBILE_MAX_FRAMES) {
          return;
        }
        frame = requestAnimationFrame(step);
      };

      frame = requestAnimationFrame(step);
      return () => cancelAnimationFrame(frame);
    },
    [scrollElementRef]
  );

  return useScrollRestore({ gridId, ready, capturePosition, applyPosition });
};

/**
 * Companion to the scroll restore hooks for a list's own UI state (search text,
 * filters, ...), following the same policy: the caller keeps listStateRef.current
 * set to its current JSON-serialisable state and saves it alongside
 * saveScrollAnchor (via the returned saveListState), it is also saved on any
 * in-app tab change, it comes back only on a genuine "back" arrival, and every
 * mount consumes it. Unlike the scroll position it is read during the first
 * render, so the list can start from it before its rows load.
 *
 * @param {object} params
 * @param {string} params.gridId  same id passed to the scroll restore hook
 * @returns {{ restoredState: *, listStateRef: object, saveListState: () => void }}
 */
export const useListStateRestore = ({ gridId }) => {
  const navigationType = useNavigationType();
  const tabs = useTabs();
  const tabKey = tabs?.activeTab?.key;
  const stateKey = stateKeyFor(tabKey, gridId);

  // Read before useScrollRestore consumes the back marker (it does so in an
  // effect, after this first render).
  const restoredState = useRef(undefined);
  if (restoredState.current === undefined) {
    const backRequested = sessionStorage.getItem(backKeyFor(tabKey, gridId)) === '1';
    let savedState = null;
    if (navigationType === 'POP' || backRequested) {
      try {
        savedState = JSON.parse(sessionStorage.getItem(stateKey));
      } catch {
        savedState = null;
      }
    }
    restoredState.current = savedState;
  }

  // One-shot: every mount consumes the saved state.
  const initialStateKey = useRef(stateKey).current;
  useEffect(() => {
    sessionStorage.removeItem(initialStateKey);
  }, [initialStateKey]);

  const listStateRef = useRef(null);

  const saveListState = useCallback(() => {
    if (listStateRef.current != null) sessionStorage.setItem(stateKey, JSON.stringify(listStateRef.current));
  }, [stateKey]);

  useEffect(() => {
    window.addEventListener('tabchange', saveListState);
    return () => window.removeEventListener('tabchange', saveListState);
  }, [saveListState]);

  return { restoredState: restoredState.current, listStateRef, saveListState };
};
